/**
 * Phase 23 (Plan 23-02) — AlertsRecentWidget.
 *
 * 5 most recent New|Acknowledged alerts visible to the caller. Reuses the
 * severity icon + truncated message + relative time shape from
 * components/notifications/AlertRow.tsx.
 */

import {
  BellIcon,
  WarningCircleIcon,
  WarningOctagonIcon,
} from '@phosphor-icons/react';
import type { JSX } from 'react';

import {
  WidgetCard,
  WidgetEmpty,
  WidgetError,
  WidgetSkeleton,
} from '@/components/dashboard/widget-states';
import type { ApiError } from '@/lib/api/error';
import { formatRelative } from '@/lib/utils/relative-time';
import type { AlertSeverity, AlertsRecentBlock, AlertSummary } from '@/types/dashboard';

export interface AlertsRecentWidgetProps {
  readonly data: AlertsRecentBlock | null | undefined;
  readonly isLoading: boolean;
  readonly error: ApiError | null;
  readonly onRetry: () => void;
  readonly className?: string;
}

function severityIconFor(severity: AlertSeverity): typeof BellIcon {
  if (severity === 'Critical') return WarningOctagonIcon;
  if (severity === 'Warning') return WarningCircleIcon;
  return BellIcon;
}

function severityColorFor(severity: AlertSeverity): string {
  if (severity === 'Critical') return 'text-destructive';
  if (severity === 'Warning') return 'text-yellow-500';
  return 'text-muted-foreground';
}

interface RowProps {
  readonly alert: AlertSummary;
}

function AlertRollupRow({ alert }: RowProps): JSX.Element {
  const Icon = severityIconFor(alert.severity);
  const color = severityColorFor(alert.severity);
  return (
    <div
      className="hover:bg-accent/50 flex items-start gap-2 rounded-md px-3 py-2"
      data-testid={`alert-summary-row-${String(alert.id)}`}
    >
      <Icon
        size={16}
        weight="duotone"
        className={`mt-0.5 shrink-0 ${color}`}
      />
      <div className="min-w-0 flex-1">
        {/*
         * Client feedback (2026-09): the same single-line `truncate` that hid the
         * detail in the bell dropdown. Backend messages are multi-line (Reason /
         * Remarks on their own lines), so the collapse happened before truncation
         * even applied. `whitespace-pre-line` keeps the structure and `line-clamp-2`
         * bounds the row on this denser dashboard card; `title` exposes the rest.
         * AlertSummary carries no notificationType, so unlike the bell row there is
         * no type label to show here without widening the dashboard DTO.
         */}
        <p
          className="line-clamp-2 whitespace-pre-line text-sm"
          title={alert.message}
          data-testid={`alert-summary-message-${String(alert.id)}`}
        >
          {alert.message}
        </p>
        <p className="text-muted-foreground text-xs">
          {formatRelative(alert.createdAt)}
        </p>
      </div>
    </div>
  );
}

interface BodyProps {
  readonly data: AlertsRecentBlock;
}

function AlertsRecentBody({ data }: BodyProps): JSX.Element {
  return (
    <div className="space-y-1" data-testid="alerts-recent-list">
      {data.items.map((alert) => (
        <AlertRollupRow key={alert.id} alert={alert} />
      ))}
    </div>
  );
}

export function AlertsRecentWidget({
  data,
  isLoading,
  error,
  onRetry,
  className,
}: AlertsRecentWidgetProps): JSX.Element {
  return (
    <WidgetCard
      title="Recent Alerts"
      testId="alerts-recent-widget"
      {...(className !== undefined ? { className } : {})}
    >
      {isLoading ? (
        <WidgetSkeleton testId="alerts-recent-skeleton" rows={3} />
      ) : error ? (
        <WidgetError
          error={error}
          onRetry={onRetry}
          testId="alerts-recent-error"
        />
      ) : !data || data.items.length === 0 ? (
        <WidgetEmpty
          message="No recent alerts."
          testId="alerts-recent-empty"
        />
      ) : (
        <AlertsRecentBody data={data} />
      )}
    </WidgetCard>
  );
}
